import React, { useState } from "react";
import { isValidName } from "@/utils";
import { useProfileUpdate, parseDob } from "@/hooks/useMembers";

import FormHeader from "@/components/ui/organisms/MHeader";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { IconCaretDownFilled } from "@tabler/icons-react";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";

import { genderOptions, isChild } from "@/types/member";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

import { PetType } from "@/types/member";
import { useNavigate } from "react-router-dom";
import { goBackOneLevel } from "@/utils/nav-utils";
import { MButton } from "@/components/ui/atoms/MButtons";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useManagedMembersContext } from "@/context/ManagedMembersContext";

const Identity: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const isPet =
    memberInfo.memberType === "p" || memberInfo.derivedMemberType === "P";

  // Pet-specific state
  const petAttrs = memberInfo.otherAttrs || memberInfo.other_attrs || {};
  const [petName, setPetName] = useState(memberInfo.firstName || "");
  const [petType, setPetType] = useState(petAttrs.petType || PetType.DOG);
  const [breed, setBreed] = useState(petAttrs.breed || "");
  const [petTypeDrawerOpen, setPetTypeDrawerOpen] = useState(false);

  const { handleProfileUpdate } = useProfileUpdate();
  const { showToast } = useToast();

  const [firstName, setFirstName] = useState(memberInfo.firstName);
  const [middleName, setMiddleName] = useState(memberInfo.middleName);
  const [lastName, setLastName] = useState(memberInfo.lastName);
  const { fetchManagedMembers } = useManagedMembersContext();

  // Validation state per field
  const firstNameValid = isValidName(firstName.trim());
  const middleNameValid = !middleName || isValidName(middleName.trim());
  const lastNameValid = isValidName(lastName?.trim());
  const allNamesValid = firstNameValid && middleNameValid && lastNameValid;
  let initialDob = "";
  if (memberInfo.dob) {
    initialDob = memberInfo.dob;
  } else if (memberInfo.dobMonth && memberInfo.dobYear) {
    // Use 01 as fallback day if only month/year are present
    initialDob = `${memberInfo.dobYear}-${String(memberInfo.dobMonth).padStart(
      2,
      "0"
    )}-01`;
  }
  const [dob, setDob] = useState<string>(initialDob);
  const [gender, setGender] = useState<number | undefined>(memberInfo.gender);
  const [genderDrawerOpen, setGenderDrawerOpen] = useState(false);

  const [saving, setSaving] = useState(false);

  // Track GTM user_data_loaded event on page load
  React.useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_IDENTITY,
    });
  }, [memberInfo?.id]);

  const getIsButtonDisabled = (): boolean => {
    return (
      saving ||
      !isChanged ||
      (isPet
        ? !petName || !breed || !petType || !dob || !gender
        : !firstName || !lastName || !dob || !gender || !allNamesValid)
    );
  };

  // Normalize DOB for comparison (yyyy-mm)
  function normalizeDob(val?: string) {
    if (!val) return "";
    const [y, m] = val.split("-");
    return `${y}-${m}`;
  }

  // Store initial values for robust comparison
  const initialPetName = React.useRef(petName);
  const initialBreed = React.useRef(breed);
  const initialPetType = React.useRef(petType);
  const initialDobRef = React.useRef(dob);
  const initialGender = React.useRef(gender);
  const initialFirstName = React.useRef(firstName);
  const initialMiddleName = React.useRef(middleName);
  const initialLastName = React.useRef(lastName);

  const isChanged = isPet
    ? petName !== initialPetName.current ||
      breed !== initialBreed.current ||
      petType !== initialPetType.current ||
      normalizeDob(dob) !== normalizeDob(initialDobRef.current) ||
      gender !== initialGender.current
    : firstName !== initialFirstName.current ||
      middleName !== initialMiddleName.current ||
      lastName !== initialLastName.current ||
      normalizeDob(dob) !== normalizeDob(initialDobRef.current) ||
      gender !== initialGender.current;

  async function handleUpdate() {
    const { dob_day, dob_month, dob_year } = parseDob(dob);
    let updateData: any;
    if (isPet) {
      updateData = {
        first_name: petName.trim(),
        gender,
        dob_day,
        dob_month,
        dob_year,
        other_attrs: {
          petType,
          breed,
        },
      };
    } else {
      updateData = {
        first_name: firstName.trim(),
        middle_name: middleName?.trim(),
        last_name: lastName?.trim(),
        gender,
        dob_day,
        dob_month,
        dob_year,
      };
    }

    const result = await handleProfileUpdate({
      memberId: memberInfo.id,
      updateData,
      isChanged,
      onSavingChange: setSaving,
    });

    if (result.success) {
      // Track completion event
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_IDENTITY_COMPLETE,
      });

      await fetchManagedMembers(true);
      navigate(goBackOneLevel(location.pathname));
    } else {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    }
  }

  const getFirstNameLabel = () => {
    return memberInfo.onBehalf
      ? t("identity.ob.whatIsYourFirstName")
      : t("identity.whatIsYourFirstName");
  };

  const getLastNameLabel = () => {
    return memberInfo.onBehalf
      ? t("identity.ob.whatIsYourLastName")
      : t("identity.whatIsYourLastName");
  };

  const getGenderLabel = () => {
    return memberInfo.onBehalf
      ? t("identity.ob.whatIsYourGender")
      : t("identity.whatIsYourGender");
  };

  const getDobLabel = () => {
    return memberInfo.onBehalf
      ? t("identity.ob.whatIsYourMonthYearBirth")
      : t("identity.whatIsYourMonthYearBirth");
  };

  const getPetTypeLabel = () => {
    return petType ? t(`identity.typeOfPetMap.${petType}`) : "";
  };

  return (
    <div className="profile-page-root">
      <FormHeader
        module="Profile"
        memberInfo={memberInfo}
        learnMoreContent={
          <>
            Maatra uses the names you enter here to match uploaded documents to
            the right profile.
            <br />
            <br />
            Use the name that appears most often in your medical records. Even
            small changes, like Riya Mehta vs Riya M., can affect how documents
            are categorised.
          </>
        }
        drawerTitle="Learn more"
      />
      <MSpacer />
      <form className="flex flex-col gap-[var(--scale-padding-4xl)] pb-[40px]">
        {isPet ? (
          <>
            <MInputField
              placeholder={t("common.notAnswered")}
              value={petName}
              onChange={(e) => setPetName(e.target.value)}
              variant="Underlined"
              size="md"
              showLabel
              label={t("identity.fullName")}
            />
            <MInputField
              placeholder={t("common.notAnswered")}
              value={getPetTypeLabel()}
              label={t("identity.typeOfPet")}
              variant="Underlined"
              size="md"
              readOnly
              icon={<IconCaretDownFilled />}
              showIcon
              onClick={() => setPetTypeDrawerOpen(true)}
              aria-label="Select Pet Type"
              showLabel
            />
            <MDropDownDrawer
              open={petTypeDrawerOpen}
              onClose={() => setPetTypeDrawerOpen(false)}
              title="Select Pet Type"
              items={[
                {
                  value: PetType.DOG,
                  label: t(`identity.typeOfPetMap.${PetType.DOG}`),
                },
                {
                  value: PetType.CAT,
                  label: t(`identity.typeOfPetMap.${PetType.CAT}`),
                },
              ]}
              initialValue={petType}
              onSelect={(val) => {
                setPetType(val as PetType);
                setPetTypeDrawerOpen(false);
              }}
              variant="single"
            />
            <MInputField
              placeholder={t("common.notAnswered")}
              value={breed}
              onChange={(e) => setBreed(e.target.value)}
              variant="Underlined"
              size="md"
              showLabel
              label={t("identity.enterBreed")}
            />
            <MDateInputField
              placeholder={t("common.notAnswered")}
              value={
                dob && /^\d{4}-\d{2}$/.test(dob)
                  ? new Date(
                      Number(dob.split("-")[0]),
                      Number(dob.split("-")[1]) - 1
                    )
                  : dob && /^\d{4}-\d{2}-\d{2}$/.test(dob)
                  ? new Date(dob)
                  : undefined
              }
              onDateChange={(date) =>
                setDob(
                  date
                    ? `${date.getFullYear()}-${String(
                        date.getMonth() + 1
                      ).padStart(2, "0")}`
                    : ""
                )
              }
              variant="Underlined"
              size="md"
              showLabel
              label={t("identity.dateOfBirth")}
              hideDate={true}
              formatDate={(date?: Date) => {
                // hideDate is always true here, so render month/year only
                return date
                  ? date.toLocaleString("en-US", {
                      month: "short",
                      year: "numeric",
                    })
                  : "";
              }}
            />
            <div>
              <label className="profile-input-label">{getGenderLabel()}</label>
              <div
                className={`profile-input profile-input-value cursor-pointer ${
                  !gender ? "text-gray-400" : ""
                }`}
                onClick={() => setGenderDrawerOpen(true)}
                tabIndex={0}
                role="button"
                aria-label="Select Gender"
                style={{ minHeight: 40, display: "flex", alignItems: "center" }}
              >
                {(isPet
                  ? genderOptions.filter(
                      (opt) => opt.label !== "Prefer not to say"
                    )
                  : genderOptions
                ).find((opt) => opt.value === gender)?.label || "Not Answered"}
              </div>
              <MDropDownDrawer
                open={genderDrawerOpen}
                onClose={() => setGenderDrawerOpen(false)}
                title={t("common.selectGender")}
                items={(isPet
                  ? genderOptions.filter(
                      (opt) => opt.label !== "Prefer not to say"
                    )
                  : genderOptions
                ).map((opt) => ({
                  value: String(opt.value),
                  label: opt.label,
                }))}
                initialValue={gender !== undefined ? String(gender) : ""}
                onSelect={(val) => {
                  setGender(val ? Number(val) : undefined);
                  setGenderDrawerOpen(false);
                }}
                variant="single"
              />
            </div>
          </>
        ) : (
          <>
            <MInputField
              placeholder={t("common.notAnswered")}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              variant="Underlined"
              size="md"
              showLabel
              label={getFirstNameLabel()}
              maxLength={20}
            />
            <MInputField
              placeholder={t("common.notAnswered")}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              variant="Underlined"
              size="md"
              showLabel
              label={getLastNameLabel()}
              maxLength={20}
            />
            <MDateInputField
              placeholder={t("common.notAnswered")}
              value={
                dob && /^\d{4}-\d{2}$/.test(dob)
                  ? new Date(
                      Number(dob.split("-")[0]),
                      Number(dob.split("-")[1]) - 1
                    )
                  : dob && /^\d{4}-\d{2}-\d{2}$/.test(dob)
                  ? new Date(dob)
                  : undefined
              }
              onDateChange={(date) =>
                setDob(
                  date
                    ? `${date.getFullYear()}-${String(
                        date.getMonth() + 1
                      ).padStart(2, "0")}`
                    : ""
                )
              }
              variant="Underlined"
              size="md"
              showLabel
              initialPickerValue={
                (dob && new Date(dob)) || new Date(2000, 0, 1)
              }
              label={getDobLabel()}
              datePickerTitle="Select Month & Year"
              hideDate={true}
              formatDate={(date?: Date) => {
                // hideDate is always true here, so render month/year only
                return date
                  ? date.toLocaleString("en-US", {
                      month: "short",
                      year: "numeric",
                    })
                  : "";
              }}
              maxDate={(() => {
                // Restrict to 18+ when the member is an FO, or when their
                // existing DOB already makes them an adult — adults must not
                // be downgraded to a child profile via DOB edit.
                const existingIsAdult =
                  !!(memberInfo.dobYear && memberInfo.dobMonth) &&
                  !isChild(memberInfo);
                if (
                  memberInfo.family_member_type === "fo" ||
                  existingIsAdult
                ) {
                  const today = new Date();
                  const year = today.getFullYear() - 18;
                  const month = today.getMonth();
                  // Always last day of the month, 18 years ago
                  return new Date(year, month + 1, 0);
                }
                return undefined;
              })()}
            />

            <MInputField
              className=""
              placeholder={t("common.notAnswered")}
              value={genderOptions.find((opt) => opt.value === gender)?.label}
              label={getGenderLabel()}
              variant="Underlined"
              size="md"
              readOnly
              icon={<IconCaretDownFilled />}
              showIcon
              onClick={() => setGenderDrawerOpen(true)}
              aria-label={getGenderLabel()}
            />
            <MDropDownDrawer
              open={genderDrawerOpen}
              onClose={() => setGenderDrawerOpen(false)}
              title={t("common.selectGender")}
              items={genderOptions.map((opt) => ({
                value: String(opt.value),
                label: opt.label,
              }))}
              initialValue={gender !== undefined ? String(gender) : ""}
              onSelect={(val) => {
                setGender(val ? Number(val) : undefined);
                setGenderDrawerOpen(false);
              }}
              variant="single"
            />
          </>
        )}
      </form>
      <div className="confirm-footer">
        <MButton
          size="lg"
          variant="filled"
          text={t("common.done")}
          className="w-full"
          disabled={getIsButtonDisabled()}
          onClick={handleUpdate}
        />
      </div>
    </div>
  );
};

export default Identity;
